/**
 * Wizard UI
 *
 * Terminal UI for interactive key entry.
 * This runs in a spawned terminal window.
 */

import * as fs from 'fs-extra';
import inquirer from 'inquirer';
import chalk from 'chalk';
import { CredentialService } from '../core/service';
import { validateKey, getInstructionsForKey } from './validation';

export interface WizardConfig {
  keys: string[];
  reason?: string;
  signalPath?: string; // Optional for inline mode
}

/**
 * Display wizard banner
 */
function showBanner(reason?: string): void {
  console.clear();
  console.log('');
  console.log(chalk.magenta('┌─────────────────────────────────────────────────────┐'));
  console.log(chalk.magenta('│') + chalk.bold.white('  SecretSage Key Rotation Wizard                    ') + chalk.magenta('│'));
  console.log(chalk.magenta('└─────────────────────────────────────────────────────┘'));
  console.log('');

  if (reason) {
    console.log(chalk.yellow('  Reason:'), reason);
    console.log('');
  }
}

/**
 * Prompt for a single key value
 */
async function promptForKey(keyName: string): Promise<string | null> {
  const instructions = getInstructionsForKey(keyName);

  console.log(chalk.cyan(`\n  ${keyName}`));
  console.log(chalk.gray('  ─'.repeat(25)));

  if (instructions) {
    console.log(chalk.gray(`  ${instructions}`));
    console.log('');
  }

  const { value } = await inquirer.prompt([
    {
      type: 'password',
      name: 'value',
      message: `Paste value for ${chalk.cyan(keyName)}:`,
      mask: '*',
      validate: (input: string) => {
        if (!input || input.trim().length === 0) {
          return 'Value cannot be empty. Press Ctrl+C to cancel.';
        }

        const validation = validateKey(keyName, input.trim());
        if (!validation.valid) {
          return validation.message || 'Invalid format';
        }

        return true;
      },
    },
  ]);

  return value.trim();
}

/**
 * Run the wizard UI
 */
export async function runWizardUI(config: WizardConfig): Promise<void> {
  const service = new CredentialService();

  try {
    // Check vault exists
    const vaults = await service.hasVault();
    if (!vaults.local && !vaults.global) {
      console.log(chalk.red('\n  Error: No vault found. Run `secretsage init` first.\n'));
      if (config.signalPath) {
        await writeSignal(config.signalPath, { success: false, error: 'No vault found' });
      }
      process.exit(1);
    }

    await service.init();

    showBanner(config.reason);

    console.log(chalk.white('  The following keys need to be entered:'));
    config.keys.forEach((key, i) => {
      console.log(chalk.gray(`    ${i + 1}. `) + chalk.cyan(key));
    });

    console.log('');
    console.log(chalk.gray('  Press Ctrl+C at any time to cancel.'));

    // Collect all key values
    const values: Record<string, string> = {};

    for (const keyName of config.keys) {
      const value = await promptForKey(keyName);
      if (value === null) {
        throw new Error('User cancelled');
      }
      values[keyName] = value;
    }

    // Confirm before saving (show minimal info to prevent leakage)
    console.log('');
    console.log(chalk.yellow('  Ready to save the following keys:'));
    config.keys.forEach((key) => {
      // Only show length, not actual characters (security)
      const len = values[key].length;
      console.log(chalk.gray(`    • `) + chalk.cyan(key) + chalk.gray(` (${len} characters)`));
    });
    console.log('');

    const { confirm } = await inquirer.prompt([
      {
        type: 'confirm',
        name: 'confirm',
        message: 'Save these keys to the vault?',
        default: true,
      },
    ]);

    if (!confirm) {
      console.log(chalk.yellow('\n  Cancelled. No changes made.\n'));
      if (config.signalPath) {
        await writeSignal(config.signalPath, { success: false, error: 'User cancelled' });
      }
      process.exit(0);
    }

    // Save all keys
    console.log('');
    for (const keyName of config.keys) {
      console.log(chalk.gray(`  Encrypting ${keyName}...`));
      const metadata = config.reason
        ? { rotationHistory: [{ timestamp: new Date(), reason: config.reason }] }
        : undefined;
      await service.add(keyName, values[keyName], metadata);
    }

    console.log('');
    console.log(chalk.green('  ✓ All keys saved successfully!'));
    console.log('');
    console.log(chalk.gray('  Run `secretsage grant --all` to update your .env file.'));
    console.log('');

    // Write success signal (if running in spawned terminal mode)
    if (config.signalPath) {
      await writeSignal(config.signalPath, {
        success: true,
        keys: config.keys,
        timestamp: new Date().toISOString(),
      });
      // Give user time to read the message in spawned terminal
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.log(chalk.red(`\n  Error: ${errorMessage}\n`));
    if (config.signalPath) {
      await writeSignal(config.signalPath, { success: false, error: errorMessage });
    }
    process.exit(1);
  }
}

/**
 * Write signal file for main process
 */
async function writeSignal(signalPath: string, data: Record<string, unknown>): Promise<void> {
  await fs.writeFile(signalPath, JSON.stringify(data, null, 2));
}
